import {
  ReactElement,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { JACKPOT_SPORTSBOOK_ID } from 'utils/casino/jackpot';
import { JACKPOT_TITLE_SIZE_DIFFERENCE } from 'utils/constants/app/magic-numbers-app';
import { SEPARATED_WIDGETS } from 'utils/constants/sportsbook/generic';
import { OVERVIEW_TAB_PATH } from 'utils/constants/sportsbook/tabs-paths';
import { lazy } from 'utils/generic/lazy';
import { Betslip } from 'newelements/_Betslip';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setIsMatchStreamingTheatreModeAvailable } from 'store/actions/match-streaming';
import { getGameInfo } from 'store/selectors/game-data';
import './index.less';

type Props = {
  isEsport?: boolean;
  isLive?: boolean;
  racing?: boolean;
  configs?: {
    type: 'live' | 'prematch';
  };
  isOverview?: boolean;
};

const { Jackpot } = lazy(
  () => import(/* webpackChunkName: "jackpot" */ 'newelements/Jackpot')
);

const { MatchStreamingWidget } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-streaming-widget" */ 'newelements/Sportsbook/Widgets/MatchStreamingWidget'
    )
);

const OVERVIEW_DETECT_TOP_HEIGHT = 75;

export const DesktopRight = (props: Props): ReactElement => {
  const skipImportEffect = useRef<boolean>(false);

  const {
    params: { sport, game }
  } = useSbRouteMatch(SEPARATED_WIDGETS.SEPARATED_WIDGETS_ROUTE);

  const dispatch = useDispatch();
  const state = useSelector(getGameInfo);
  const sportsBookConfigs = useSportsbookConfigs();

  const jackpotTitleFontSize = sportsBookConfigs?.jackpotFontSize
    ? (
        sportsBookConfigs.jackpotFontSize - JACKPOT_TITLE_SIZE_DIFFERENCE
      ).toString()
    : '18';

  const jackpotDetailsFontSize = sportsBookConfigs?.jackpotFontSize
    ? sportsBookConfigs.jackpotFontSize
    : '24';

  const overviewPath = location.pathname.includes(OVERVIEW_TAB_PATH);

  const showMatchStreamingContent =
    props.racing || props.isLive || props.isEsport || state.game.type === 1;

  const isMatchStreamingAvailable =
    !overviewPath &&
    showMatchStreamingContent &&
    !SEPARATED_WIDGETS.IS_SEPARATED_WIDGETS;

  useEffect(() => {
    if (
      !skipImportEffect.current &&
      (props.isLive || state?.game?.type === 1)
    ) {
      skipImportEffect.current = true;
    }
  }, [state]);

  useLayoutEffect(() => {
    const isEsportSeparatedWidgetMatchStreaming =
      SEPARATED_WIDGETS.IS_SEPARATED_ESPORT_WIDGETS && game;

    const isEsport =
      (props.isEsport && !SEPARATED_WIDGETS.IS_SEPARATED_ESPORT_WIDGETS) ||
      isEsportSeparatedWidgetMatchStreaming;

    if (props.isEsport || isEsport) {
      dispatch(setIsMatchStreamingTheatreModeAvailable(true));
    }
  }, [
    sport,
    props.racing,
    props.isEsport,
    ...(SEPARATED_WIDGETS.IS_SEPARATED_WIDGETS ? [game] : [])
  ]);

  return (
    <>
      {!!sportsBookConfigs.jackpotForSportsbook && (
        <div className="sportsbook__jackpot">
          <Suspense fallback={null}>
            <Jackpot
              configs={{
                moduleId: JACKPOT_SPORTSBOOK_ID,
                showJackpot: true,
                jackpotBackground: false,
                jackpotType: '5',
                jackpotTitleFontSize: jackpotTitleFontSize,
                jackpotDetailsFontSize: jackpotDetailsFontSize,
                jackpotBorderRadius: '6'
              }}
            />
          </Suspense>
        </div>
      )}

      {isMatchStreamingAvailable ? (
        <Suspense fallback={null}>
          <MatchStreamingWidget
            shouldHaveSportsbookWrapper
            configs={{
              isEsport: props.isEsport
            }}
          />
        </Suspense>
      ) : null}

      <Betslip
        jackpotForSport
        detectTopHeight={props.isOverview ? OVERVIEW_DETECT_TOP_HEIGHT : 0}
      />
    </>
  );
};
